// Invoice queue — card gallery (app.md §8, redesigned v1.20). One card per
// invoice: a clickable header row (chevron · company eyebrow over vendor title
// with a date/external-id meta line · age + flags or amounts · actions) that
// expands to the inline line items sub-table (lazy-loaded). A slim toolbar above
// the list carries Select all (bulk screens) and Expand all / Collapse all —
// the old table-header controls, relocated. Two right-cluster variants:
// 'intake' (Import Control: age pill + unresolved-flags pill or status badge)
// and 'amounts' (downstream steps: headline Total over Subtotal + Tax; the age
// pill moves into the meta line). Optional selection checkboxes drive bulk
// actions; per-row buttons (Approve/Deny/Confirm/Archive…) come in via
// rowActions, rendered beside the Open button. An optional grand-total footer
// sums the displayed rows' amounts (Export Control — app.md §9).
//
// PORT NOTE: a row no longer shows "Submitting…". Decisions are synchronous —
// the write returns the new status — so a row is either still here or already
// gone, and there is no in-between state to render. The flag chips read the
// row's own unresolved flag types too; the queue used to fire a second query.
import { useEffect, useState, type ReactNode } from 'react'
import {
  Button,
  Checkbox,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  ChevronDoubleDownRegular,
  ChevronDoubleUpRegular,
  ChevronRightRegular,
  OpenRegular,
} from '@fluentui/react-icons'
import type { InvoiceLineItem, Invoice } from '../lib/types'
import { loadLineItems, unresolvedFlagTypes } from '../lib/data'
import { flagLabel, InvoiceStatus } from '../lib/constants'
import { TERMINAL_STATUSES } from '../lib/metrics'
import { ageInDays, formatCurrency, formatDate } from '../lib/format'
import { TINT_PILLS } from '../lib/theme'
import { AgePill, DotPill, StatusBadge, UnresolvedFlagsPill } from './Badges'
import { InvoiceLineItemsTable } from './InvoiceLineItemsTable'

const useStyles = makeStyles({
  toolbar: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' },
  spacer: { flexGrow: 1 },
  list: { display: 'flex', flexDirection: 'column', gap: '12px' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    boxShadow: tokens.shadow2,
    overflow: 'hidden',
    transitionProperty: 'box-shadow',
    transitionDuration: '150ms',
    '&:hover': { boxShadow: tokens.shadow4 },
  },
  dimmed: { opacity: 0.55 },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '12px 16px',
    cursor: 'pointer',
    '&:hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  // Light brand tint marks the expanded card's header as part of the open group.
  headOpen: {
    backgroundColor: '#F5F9FD',
    '&:hover': { backgroundColor: '#F0F6FC' },
  },
  chev: {
    display: 'flex',
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    transitionProperty: 'transform',
    transitionDuration: '150ms',
    '@media (prefers-reduced-motion: reduce)': { transitionProperty: 'none' },
  },
  chevOpen: { transform: 'rotate(90deg)' },
  // The expander is a real button (the card header is not) — trim it to the
  // glyph so the row still reads as a chevron.
  chevBtn: { minWidth: '24px', padding: 0, flexShrink: 0 },
  tgroup: { flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' },
  eyebrow: {
    fontSize: '10.5px',
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground3,
  },
  vendor: {
    fontSize: '15px',
    fontWeight: tokens.fontWeightSemibold,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12.5px',
    color: tokens.colorNeutralForeground3,
  },
  rgroup: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
  amounts: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '2px',
    fontVariantNumeric: 'tabular-nums',
  },
  totalLine: { fontSize: '15px', fontWeight: tokens.fontWeightSemibold },
  subTaxLine: { fontSize: '12px', color: tokens.colorNeutralForeground3 },
  actions: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
  // 0fr→1fr grid trick animates the expansion without measuring heights.
  exp: {
    display: 'grid',
    gridTemplateRows: '0fr',
    transitionProperty: 'grid-template-rows',
    transitionDuration: '180ms',
    '@media (prefers-reduced-motion: reduce)': { transitionProperty: 'none' },
  },
  expOpen: { gridTemplateRows: '1fr' },
  clip: { overflow: 'hidden', minHeight: 0 },
  lineItemsWrap: {
    margin: '0 16px',
    padding: '10px 0 14px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  totalsBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginTop: '12px',
    padding: '10px 16px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
  },
})

interface Props {
  invoices: Invoice[]
  /** 'intake' (default) = Age / Flags-or-Status; 'amounts' = Total over Subtotal + Tax. */
  columns?: 'intake' | 'amounts'
  selectable?: boolean
  selectedIds?: Set<string>
  onToggle?: (id: string) => void
  onToggleAll?: () => void
  allSelected?: boolean
  /** Show the unresolved-flags pill instead of the status badge (Import
      Control). The types come off the row itself. */
  showFlags?: boolean
  onOpen?: (invoice: Invoice) => void
  /** Per-row action buttons rendered before Open (Approve/Deny/Confirm/Archive…). */
  rowActions?: (invoice: Invoice) => ReactNode
  /** Per-row selection veto: return the reason (tooltip) to disable the
      checkbox — not authorized, on hold, … — or undefined to allow. */
  selectionDisabled?: (invoice: Invoice) => string | undefined
  /** Cards rendered dimmed (on hold today). Purely visual. */
  dimmedIds?: Set<string>
  /** Invoices frozen by a hold — they carry an "On hold" pill, because dimming
      and an amber lock glyph have no text equivalent on their own. Separate
      from dimmedIds, which callers may use for other states. */
  heldIds?: Set<string>
  /** Bump to re-read the expanded cards' line items — the queue's own reloads
      no longer invalidate them (see the cache effect below). Hosts that edit
      lines from the detail modal bump this on save. */
  lineItemsNonce?: number
  /** When set (amounts columns only), render a footer summing the displayed
      rows' Subtotal/Tax/Total under this label (Export Control — app.md §9). */
  totalsLabel?: string
}

export function InvoiceQueue({
  invoices,
  columns = 'intake',
  selectable,
  selectedIds,
  onToggle,
  onToggleAll,
  allSelected,
  showFlags,
  onOpen,
  rowActions,
  selectionDisabled,
  dimmedIds,
  heldIds,
  lineItemsNonce = 0,
  totalsLabel,
}: Props) {
  const s = useStyles()
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [lineItemsById, setLineItemsById] = useState<Record<string, InvoiceLineItem[] | 'loading'>>({})

  const hasActions = !!(onOpen || rowActions)
  const allExpanded = invoices.length > 0 && invoices.every((i) => expandedIds.has(i.id))

  // Cached line items are invalidated when the rows actually change — a modal
  // save may have changed them, and a stale cache showed pre-edit lines (and a
  // bogus "doesn't reconcile" chip). Key on a stable signature (each row's id +
  // its last-write stamp) plus the host's lineItemsNonce, NOT the invoices array
  // identity: a reload hands back a fresh array of the same unchanged rows, and
  // keying on identity dumped and refetched every expanded card each time.
  const lineItemsKey =
    invoices.map((i) => `${i.id}:${i.updated_at ?? ''}`).join(',') + `#${lineItemsNonce}`
  useEffect(() => {
    const stillExpanded = invoices.filter(
      (i) => expandedIds.has(i.id) && i.id,
    )
    setLineItemsById(() => {
      const next: Record<string, InvoiceLineItem[] | 'loading'> = {}
      stillExpanded.forEach((i) => { next[i.id] = 'loading' })
      return next
    })
    stillExpanded.forEach((i) => {
      loadLineItems(i.id)
        .then((c) => setLineItemsById((p) => ({ ...p, [i.id]: c })))
        .catch(() => setLineItemsById((p) => ({ ...p, [i.id]: [] })))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on lineItemsKey (stable row signature), not the invoices array identity
  }, [lineItemsKey])

  function loadLineItemsFor(inv: Invoice) {
    const id = inv.id
    if (lineItemsById[id] || !inv.id) return
    setLineItemsById((p) => ({ ...p, [id]: 'loading' }))
    loadLineItems(inv.id)
      .then((c) => setLineItemsById((p) => ({ ...p, [id]: c })))
      .catch(() => setLineItemsById((p) => ({ ...p, [id]: [] })))
  }

  function toggleExpand(inv: Invoice) {
    const id = inv.id
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    if (!expandedIds.has(id)) loadLineItemsFor(inv)
  }

  function toggleExpandAll() {
    if (allExpanded) {
      setExpandedIds(new Set())
      return
    }
    setExpandedIds(new Set(invoices.map((i) => i.id)))
    invoices.forEach(loadLineItemsFor)
  }

  return (
    <div aria-label="Invoice queue">
      <div className={s.toolbar}>
        {selectable && (
          <Checkbox
            checked={allSelected}
            onChange={() => onToggleAll?.()}
            label="Select all"
            aria-label="Select all"
          />
        )}
        <div className={s.spacer} />
        <Button
          appearance="subtle"
          size="small"
          icon={allExpanded ? <ChevronDoubleUpRegular /> : <ChevronDoubleDownRegular />}
          onClick={toggleExpandAll}
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </Button>
      </div>

      <div className={s.list} role="list">
        {invoices.map((inv) => {
          const id = inv.id
          const expanded = expandedIds.has(id)
          const days = ageInDays(inv.invoice_date)
          const flagTypes = unresolvedFlagTypes(inv)
          const unresolvedCount = inv.unresolved_flag_count ?? flagTypes.length
          const selectionVeto = selectionDisabled?.(inv)
          const dimmed = dimmedIds?.has(id)
          const held = heldIds?.has(id)
          const vendorLabel = inv.vendor_name ?? 'invoice'
          return (
            <div key={id} className={mergeClasses(s.card, dimmed && s.dimmed)} role="listitem">
              {/* The header is a plain container, not a button: it holds the
                  selection checkbox, the expander and the row actions, and a
                  button may not contain other controls. Clicking the empty
                  space still expands the card; the chevron button is the
                  keyboard/screen-reader route. */}
              <div
                className={mergeClasses(s.head, expanded && s.headOpen)}
                onClick={() => toggleExpand(inv)}
              >
                {selectable && (
                  <span title={selectionVeto} onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds?.has(id) ?? false}
                      onChange={() => onToggle?.(id)}
                      disabled={!!selectionVeto}
                      // The veto rides the accessible name: a disabled checkbox
                      // is not focusable, so a title tooltip alone never
                      // reaches anyone who isn't hovering it.
                      aria-label={selectionVeto ? `Select invoice — unavailable: ${selectionVeto}` : 'Select invoice'}
                    />
                  </span>
                )}
                <Button
                  appearance="subtle"
                  size="small"
                  className={s.chevBtn}
                  aria-expanded={expanded}
                  aria-label={`${expanded ? 'Hide' : 'Show'} line items for ${vendorLabel}`}
                  icon={
                    <span className={mergeClasses(s.chev, expanded && s.chevOpen)}>
                      <ChevronRightRegular />
                    </span>
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleExpand(inv)
                  }}
                />
                <div className={s.tgroup}>
                  {/* Words, not dashes: with the everything-gets-added rule a
                      junk scan legitimately arrives with neither, and a card
                      of bare em-dashes reads as broken rather than unresolved. */}
                  <span className={s.eyebrow}>{inv.company_name ?? 'Company unknown'}</span>
                  <span className={s.vendor}>{inv.vendor_name ?? 'Vendor unknown'}</span>
                  <span className={s.meta}>
                    <span>{formatDate(inv.invoice_date)}</span>
                    {columns === 'amounts' && <AgePill days={days} />}
                    {/* Marked at step 5: this invoice skips the CSV path after
                        the last approval step (app.md §9 Manual Entry). */}
                    {inv.manual_entry && (
                      <DotPill pill={TINT_PILLS.blue} title="Marked for manual Point Click Care entry — routes to Manual Entry after the last approval step instead of Export Control">
                        Manual entry
                      </DotPill>
                    )}
                    {/* Hold state in words — the dimming and the amber lock
                        glyph carry no text alternative on their own. */}
                    {held && (
                      <DotPill pill={TINT_PILLS.yellow} title={selectionVeto}>
                        On hold
                      </DotPill>
                    )}
                  </span>
                </div>
                <div className={s.rgroup}>
                  {columns === 'intake' ? (
                    <>
                      <AgePill days={days} />
                      {/* Import Control shows flags rather than a status badge,
                          because every row there is the same status — except a
                          `Matching Failed` one, which shares the queue and needs
                          saying so: its vendor, company and accounts are blank
                          not because the invoice is odd but because the matching
                          call never answered, and a reviewer reading a blank row
                          as "the model found nothing" would draw the wrong
                          conclusion about the document in front of them. */}
                      {inv.status === InvoiceStatus.MatchingFailed && <StatusBadge status={inv.status} />}
                      {showFlags ? (
                        <UnresolvedFlagsPill
                          count={unresolvedCount}
                          title={flagTypes.map(flagLabel).join(', ') || undefined}
                        />
                      ) : (
                        <StatusBadge status={inv.status} />
                      )}
                    </>
                  ) : (
                    <div className={s.amounts}>
                      {/* Archives spans five terminal statuses (paid into Point
                          Click Care vs archived), so a terminal row shows which
                          one it is. Step queues are single-status, where the
                          badge would just repeat the page title. */}
                      {TERMINAL_STATUSES.includes(inv.status ?? '') && (
                        <StatusBadge status={inv.status} />
                      )}
                      <span className={s.totalLine}>{formatCurrency(inv.total)}</span>
                      <span className={s.subTaxLine}>
                        {formatCurrency(inv.subtotal)} + {formatCurrency(inv.tax)} tax
                      </span>
                    </div>
                  )}
                  {hasActions && (
                    <div className={s.actions} onClick={(e) => e.stopPropagation()}>
                      {rowActions?.(inv)}
                      {onOpen && (
                        <Button size="small" icon={<OpenRegular />} onClick={() => onOpen(inv)}>
                          Open
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className={mergeClasses(s.exp, expanded && s.expOpen)}>
                <div className={s.clip}>
                  <div className={s.lineItemsWrap}>
                    <InvoiceLineItemsTable
                      lineItems={lineItemsById[id]}
                      subtotal={inv.subtotal}
                      tax={inv.tax}
                      total={inv.total}
                      invoiceCompanyId={inv.company_id}
                    />
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {totalsLabel && columns === 'amounts' && invoices.length > 0 && (
        <div className={s.totalsBar}>
          <span>
            {totalsLabel} — {invoices.length} invoice{invoices.length === 1 ? '' : 's'}
          </span>
          <span>
            {formatCurrency(invoices.reduce((sum, i) => sum + (i.subtotal ?? 0), 0))} Subtotal +{' '}
            {formatCurrency(invoices.reduce((sum, i) => sum + (i.tax ?? 0), 0))} Tax ={' '}
            {formatCurrency(invoices.reduce((sum, i) => sum + (i.total ?? 0), 0))} Total
          </span>
        </div>
      )}
    </div>
  )
}
